import { Button, cn } from '@up2stay/up2promo-ui';
import { CreditCard, FileText } from 'lucide-react';

import {
  BillingCard,
  BillingCardTitle,
} from '@/core/components/billing/billing-card';
import { useDictionary } from '@/core/providers/dictionary-provider';

type BillingPaymentMethodCardProps = {
  /** True when a paid or trial subscription is active (card was collected). */
  hasPaymentMethodHint: boolean;
  canManage: boolean;
  isPending?: boolean;
  onManage: () => void;
};

export function BillingPaymentMethodCard({
  hasPaymentMethodHint,
  canManage,
  isPending,
  onManage,
}: BillingPaymentMethodCardProps) {
  const overview = useDictionary().workspace.billing.overview;

  return (
    <BillingCard aria-label={overview.paymentTitle}>
      <BillingCardTitle>{overview.paymentTitle}</BillingCardTitle>
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border border-dashed border-border px-3.5 py-3.5',
        )}
      >
        <span
          className={cn(
            'flex h-7 w-10 shrink-0 items-center justify-center rounded-md',
            'bg-muted text-muted-foreground',
          )}
        >
          <CreditCard className="size-[18px]" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-foreground">
            {hasPaymentMethodHint
              ? overview.paymentManagedTitle
              : overview.paymentEmptyTitle}
          </span>
          <span className="text-xs text-muted-foreground">
            {hasPaymentMethodHint
              ? overview.paymentManagedHint
              : overview.paymentEmptyHint}
          </span>
        </span>
      </div>
      {canManage ? (
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full"
          disabled={isPending}
          onClick={onManage}
        >
          {hasPaymentMethodHint
            ? overview.paymentManageCta
            : overview.paymentAddCta}
        </Button>
      ) : null}
    </BillingCard>
  );
}

type BillingInvoicesCardProps = {
  hasPaidSubscription: boolean;
  canManage: boolean;
  canOpenPortal: boolean;
  isPending?: boolean;
  onOpenPortal: () => void;
};

export function BillingInvoicesCard({
  hasPaidSubscription,
  canManage,
  canOpenPortal,
  isPending,
  onOpenPortal,
}: BillingInvoicesCardProps) {
  const billingDict = useDictionary().workspace.billing;
  const overview = billingDict.overview;
  const invoices = billingDict.invoices;

  return (
    <BillingCard aria-label={overview.invoicesTitle}>
      <BillingCardTitle>{overview.invoicesTitle}</BillingCardTitle>
      {canOpenPortal ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            {hasPaidSubscription
              ? invoices.description
              : invoices.descriptionHistory}
          </p>
          {canManage ? (
            <Button
              type="button"
              variant="outline"
              className="h-11 w-full"
              disabled={isPending}
              onClick={onOpenPortal}
            >
              {hasPaidSubscription
                ? invoices.openPortal
                : invoices.openPortalHistory}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 px-2 py-5 text-center">
          <span
            className={cn(
              'flex size-11 items-center justify-center rounded-xl',
              'bg-muted text-muted-foreground',
            )}
          >
            <FileText className="size-5" aria-hidden />
          </span>
          <span className="text-sm font-medium text-foreground">
            {overview.invoicesEmptyTitle}
          </span>
          <span className="max-w-[240px] text-xs leading-snug text-muted-foreground">
            {overview.invoicesEmptyBody}
          </span>
        </div>
      )}
    </BillingCard>
  );
}
